import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { BASE_URL } from "utils";

// ─── 강제 로그아웃 ─────────────────────────────────────────────────────────────
export const forceLogout = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("buccl_admin");
  window.location.href = "/login";
};

// ─── axios 인스턴스 ────────────────────────────────────────────────────────────
const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// ─── 401 연속 실패 카운터 ─────────────────────────────────────────────────────
let unauthorizedCount = 0;
const MAX_UNAUTHORIZED = 3;

// ─── refresh 중복 요청 방지 ────────────────────────────────────────────────────
let isRefreshing = false;
type RefreshSubscriber = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};
let refreshSubscribers: RefreshSubscriber[] = [];

// refresh 성공 → 대기 중인 요청들에 새 토큰 전달
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(({ resolve }) => resolve(token));
  refreshSubscribers = [];
};

// refresh 실패 → 대기 중인 요청들도 함께 실패 처리 (영구 hang 방지)
const onRefreshFailed = (error: unknown) => {
  refreshSubscribers.forEach(({ reject }) => reject(error));
  refreshSubscribers = [];
};

// ─── 요청 인터셉터: Authorization 헤더 주입 ───────────────────────────────────
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ─── 응답 인터셉터 ─────────────────────────────────────────────────────────────
axiosInstance.interceptors.response.use(
  (response) => {
    // 백엔드 자동 갱신 토큰 처리 (x-token-refreshed 헤더)
    const isTokenRefreshed = response.headers["x-token-refreshed"];
    const newAccessToken = response.headers["x-new-access-token"];
    if (["true", "1"].includes(String(isTokenRefreshed ?? "").toLowerCase()) && newAccessToken) {
      // refresh는 HttpOnly 쿠키에서 백엔드가 관리하므로 access만 동기화
      localStorage.setItem("access_token", newAccessToken);
    }
    // 정상 응답이 오면 연속 실패 카운터 리셋
    unauthorizedCount = 0;
    return response;
  },
  async (error) => {
    if (!error.response) return Promise.reject(error);

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response.status === 401) {
      // 이미 retry한 요청이 또 401 → 갱신해도 소용 없음 → 강제 로그아웃
      if (originalRequest._retry) {
        forceLogout();
        return Promise.reject(error);
      }

      // refresh 요청이 이미 진행 중이면 완료될 때까지 대기 (중복 갱신 방지)
      // 카운터를 올리기 전에 분기해야 동시다발 401이 잘못된 로그아웃을 유발하지 않음
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          refreshSubscribers.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        });
      }

      unauthorizedCount += 1;

      // 갱신 시도가 연속 MAX_UNAUTHORIZED회 실패하면 강제 로그아웃
      if (unauthorizedCount >= MAX_UNAUTHORIZED) {
        forceLogout();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // refresh 토큰은 HttpOnly 쿠키로 전송되므로 withCredentials만 있으면 됨
        const { data } = await axios.post<{ access: string }>(`${BASE_URL}/server/buccl_user/auth/refresh/`, {}, { withCredentials: true });
        const newAccess = data.access;
        localStorage.setItem("access_token", newAccess);
        unauthorizedCount = 0;
        onTokenRefreshed(newAccess);
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // refresh도 만료/실패 → 대기 중인 요청들도 함께 실패시키고 강제 로그아웃
        onRefreshFailed(refreshError);
        forceLogout();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
